import { Injectable } from '@angular/core';
import { Company } from '@core/models/common/company';
import { User } from '@core/models/user/user';
import { isDefined } from '@core/utils/object';
import { WindowRef } from '@vendor/window/window.component';
import { INTERCOM_ACTION } from './intercom-action';
import { environment } from '@environments/environment';
import { ClientConfigDataService } from '@core/data/api/config/client-config-data.service';

@Injectable({ providedIn: 'root' })
export class IntercomService {

    public company: Company;
    public user: User;
    private nativeWindow: Window;

    constructor(
        windowRef: WindowRef,
        private clientConfigDataService: ClientConfigDataService
    ) {
        this.nativeWindow = windowRef.nativeWindow;
    }
    
    public getIntercom() {
        return (this.nativeWindow as any)?.Intercom || (() => {});
    }

    public boot(user: User, company: Company, userHash?: string) {
        const intercom = this.getIntercom();

        const appId = this.clientConfigDataService.getIntercomAppId() || environment.intercom.app_id;

        const config = Object.assign({
            app_id: appId
        }, this.getSessionConfig(user, company, userHash));

        intercom(INTERCOM_ACTION.BOOT, config);
    }

    public trackEvent(eventName: string, metadata?: any) {
        const intercom = this.getIntercom();

        if (isDefined(this.nativeWindow) && isDefined(intercom)) {
            intercom(INTERCOM_ACTION.TRACK_EVENT, eventName, metadata);
        }
    }

    public setUser(user: User, company: Company) {
        const intercom = this.getIntercom();

        if (intercom) {
            intercom(INTERCOM_ACTION.UPDATE, this.getSessionConfig(user, company))
        }
    }

    public showChat() {
        const intercom = this.getIntercom();

        intercom(INTERCOM_ACTION.SHOW);
    }

    public hideChat() {
        const intercom = this.getIntercom();

        intercom(INTERCOM_ACTION.HIDE);
    }

    public shutDown() {
        const intercom = this.getIntercom();

        intercom(INTERCOM_ACTION.SHUTDOWN);
    }

    private getSessionConfig(user: User, company: Company, userHash?: string) {

        const config: any = {
            user_id: user.id,
            name: user.name,
            email: user.email,
            phone: user.phone,
            company: {
                id: company.id,
                name: company.name
            },
            company_role: company.role,
            company_name: company.name,
            company_id: company.id,
            app_version: (this.nativeWindow as any).PAY_VERSION || ''
        };

        // Identity Verification — server-provided user_hash from /v3/client-config/identity
        if (userHash) {
            config.user_hash = userHash;
        }

        return config;
    }
}